import React, {
  useContext,
  useState,
} from 'react'
import PlacesAutocomplete from 'react-places-autocomplete'

import { GoogleMapsContext } from 'utils/apis/googleGeocoding'

import Loading from 'components/base/Loading'

import { InputField } from 'components/base/Form/Input/Input/styles'

import { Icon } from 'components/base/Form/Input/types'

import { Type } from 'components/base/Form/Input/Input/types'

import { Props } from './types'

import {
  Suggestion,
  Suggestions,
} from './styles'

const Address: React.FC<Props> = ({
  disabled: disabledProp = false,
  isSuggestionSelected,
  loading = false,
  onBlur = (): void => undefined,
  onChange,
  onSelect,
  showSuggestions = true,
  valid,
  value = '',
  ...props
}): React.ReactElement | null => {
  const { isLoaded: isGoogleMapsLoaded } = useContext(GoogleMapsContext)

  const disabled = disabledProp || loading

  const [
    errorText,
    setErrorText,
  ] = useState('')

  const [
    organicValue,
    setOrganicValue,
  ] = useState(value)

  if (!isGoogleMapsLoaded) {
    return <Loading />
  }

  const handleError = (status: string, clearSuggestion: Function): void => {
    clearSuggestion()
  }

  /**
   * This fires when someone clicks on a suggestion or presses the enter key from within the input
   */
  const handleSelect = async (value: string, placeId?: string): Promise<void> => {
    setErrorText('')

    // Only fire if a suggestion was clicked (not if enter key was pressed)
    if (placeId) {
      onSelect(value)
    }
  }

  /**
   * This fires when someone moves their cursor to a different suggestion in the dropdown, or enters different text into the input
   */
  const handleChange = (value: string): void => {
    setErrorText('')

    onChange(value)
  }

  const handleBlur = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    // If no selection has been made, revert to organically-typed-in value
    if (!isSuggestionSelected) {
      onChange(organicValue)
    }
    else {
      const value = event.target.value || organicValue

      setErrorText((!value && 'Please make a selection.') || '')
    }

    onBlur(value)
  }

  if (!showSuggestions) {
    return <InputField {...props} componentType='address' onChange={onChange} />
  }

  const inputProps = {
    ...props,
    componentType: 'address',
    errorText,
    icon: Icon.pin,
    touched: props.touched,
    valid,
    value,
  }

  return (
    <>
      <PlacesAutocomplete
        {...props}
        debounce={250}
        value={value.toString()}
        onChange={handleChange}
        onError={handleError}
        onSelect={handleSelect}
      >
        {({
          getInputProps,
          getSuggestionItemProps,
          suggestions,
        }): React.ReactElement => (
          <>
            <InputField
              {...inputProps}
              {...getInputProps({
                autoComplete: inputProps.autoComplete || inputProps.name || 'off',
                disabled,
                onBlur: handleBlur,
                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>): void => {
                  const fixedEvent = Object.assign(event, { code: 'makeTypescriptHappy' })

                  getInputProps().onKeyDown(fixedEvent)
                },
                onKeyUp: (event: React.KeyboardEvent<HTMLInputElement>): void => {
                  if (![
                    'ArrowDown',
                    'ArrowUp',
                    'Down',
                    'Up',
                  ].includes(event.key)) {
                    const input = event.target as HTMLInputElement

                    setOrganicValue(input.value)
                  }
                },
              })}
              type={Type.text}
            />

            {(suggestions.length && (
              <Suggestions>
                {suggestions.map(suggestion => (
                  <Suggestion
                    {...suggestion}
                    {...getSuggestionItemProps(suggestion, {})}
                    key={suggestion.id || suggestion.placeId}
                  >
                    <span>{suggestion.description}</span>
                  </Suggestion>
                ))}
              </Suggestions>
            )) ||
              undefined}
          </>
        )}
      </PlacesAutocomplete>
    </>
  )
}

export * from './types'

export default Address
